import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (task-style builder layout from the design system)
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Edit3, GripVertical, Plus, Trash2, X, Zap, ShieldCheck,
  AlertTriangle, CheckCircle2, CalendarRange,
} from 'lucide-react';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { Skeleton } from '../components/Skeleton';
import RuleBuilder, { summarizeRule, type RuleGroup, type RuleQuestion } from '../components/RuleBuilder';
import OptionSourceEditor, {
  StaticOptionsEditor, normalizeOptionSource,
  type OptionSource, type OptionSourceType,
} from '../components/OptionSourceEditor';
import { onboardingAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Grow → Onboarding Setup → Checklist.
 *
 * Authors the questions a NEW JOINER answers themselves, day by day, across their
 * onboarding plan. A form is valid for a RANGE of JOURNEY days — Day 0–1 for one
 * form, Day 2–5 for the next — so the questions can change as the fortnight
 * progresses. Days are 0-based and relative to that joiner: **Day 0 is their
 * JOINING day** (the same numbering the onboarding plan uses), whatever calendar
 * date that is. That relativity is exactly why this could not be a Task — a task
 * row carries one shared start/end date for everyone it targets.
 *
 * The sibling of the sign-off form, flipped: the JOINER answers here (self), the
 * BUDDY answers the sign-off; and these are JOURNEY days, the sign-off's are
 * shadow-training days.
 *
 * The question editor deliberately mirrors Tasks and imports the very same
 * primitives — RuleBuilder, OptionSourceEditor, StaticOptionsEditor — so the two
 * behave identically and share one `rules` JSONB contract. The editor SHELL is
 * re-implemented rather than lifted out of TasksPage: that page's editor is a
 * closure over a dozen of its own state slots, and extracting it would mean
 * refactoring a live, heavily-used screen as a side effect of this feature.
 */

type QuestionType =
  | 'text' | 'number' | 'single_choice' | 'multiple_choice' | 'dropdown'
  | 'date' | 'photo' | 'file' | 'rating_1_5';

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'photo', label: 'Photo' },
  { value: 'file', label: 'File Upload' },
  { value: 'rating_1_5', label: 'Rating' },
];

const CHOICE_TYPES: QuestionType[] = ['single_choice', 'multiple_choice', 'dropdown'];

// Per-type accent on the question card's left border — the same quick visual cue
// Tasks uses, so an admin moving between the two reads them the same way.
const TYPE_ACCENT: Record<QuestionType, string> = {
  text: '#94A3B8',
  number: '#3B82F6',
  dropdown: '#6366F1',
  single_choice: '#14B8A6',
  multiple_choice: '#A855F7',
  date: '#F59E0B',
  photo: '#E11D48',
  file: '#0D9488',
  rating_1_5: '#0EA5E9',
};

interface ValidationRules {
  format?: 'letters' | 'numbers' | 'alphanumeric' | 'email' | 'integer' | 'positive' | 'regex' | null;
  regex?: string | null;
  length?: { min?: number | null; max?: number | null } | null;
  dateFormat?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY' | 'long' | null;
  unique?: { scope: 'question' | 'form' | 'project' | 'global' } | null;
  errorMessage?: string | null;
}

interface QuestionRules {
  visibility?: RuleGroup;
  option_source?: OptionSource;
  validation?: ValidationRules;
}

interface ChecklistForm {
  id: number;
  title: string;
  description: string | null;
  fromDay: number;
  toDay: number;
  active: boolean;
  questionCount?: number;
}

interface ChecklistQuestion {
  id: string;
  questionText: string;
  questionType: QuestionType;
  options: string[] | null;
  isRequired: boolean;
  displayOrder: number;
  rules: QuestionRules | null;
  ratingMax: number;
  ratingDisplayMode: string;
  ratingStep: number;
}

/**
 * Plain-English reading of a day range.
 *
 * Days are 0-based JOURNEY days, exactly like the onboarding plan: **Day 0 is the
 * joiner's JOINING day**, Day 1 the next day, and so on — whatever calendar date
 * that is. So a form for "Day 0–1" is asked on the joining day and the day after.
 * This gloss renders live beside the inputs so the reader need not hold the base
 * in their head.
 */
const describeRange = (fromRaw: string, toRaw: string): string => {
  const from = Number(fromRaw);
  const to = Number(toRaw);
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) {
    return 'Set a valid day range.';
  }
  // Day 1 is the joining day (1-based display); Day 2 is the day after joining.
  const label = (d: number) =>
    d === 1 ? 'the joining day'
      : d === 2 ? 'the day after joining'
      : `day ${d} of onboarding`;
  if (from === to) return `Asked on ${label(from)}.`;
  const count = to - from + 1;
  if (from === 1) return `Asked on the joining day and the ${count - 1} day${count - 1 === 1 ? '' : 's'} after.`;
  return `Asked from ${label(from)} through ${label(to)} — ${count} days.`;
};

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const httpStatus = (err: unknown): number | null =>
  (err as { response?: { status?: number } })?.response?.status ?? null;

const emptyQuestionForm = () => ({
  question_text: '',
  // Default to a choice type so the Options section is visible immediately —
  // picking Static vs Depends-on-another-question is the primary decision.
  question_type: 'dropdown' as QuestionType,
  options: [''],
  is_required: true,
  options_source_type: 'static' as OptionSourceType,
  options_source: null as OptionSource | null,
  visibility_rules: null as RuleGroup | null,
  validation_format: 'none' as 'none' | NonNullable<ValidationRules['format']>,
  validation_regex: '',
  validation_length_min: '' as number | '',
  validation_length_max: '' as number | '',
  validation_date_format: 'DD/MM/YYYY' as NonNullable<ValidationRules['dateFormat']>,
  validation_unique_scope: 'none' as 'none' | 'question' | 'form' | 'project' | 'global',
  validation_error_message: '',
  rating_display_mode: 'buttons' as 'buttons' | 'stars' | 'slider' | 'dropdown',
  rating_step: 1 as 0.5 | 1,
  rating_max: '5' as string,
});
type QForm = ReturnType<typeof emptyQuestionForm>;

export default function OnboardingChecklistFormPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const [forms, setForms] = useState<ChecklistForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The builder modal — open with a form id (edit) or null-id (create).
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<ChecklistForm | null>(null);
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([]);
  const [basicOpen, setBasicOpen] = useState(true);

  // fromDay/toDay here are 0-based JOURNEY days (Day 0 = the joining day) — the
  // same convention the onboarding plan uses, and what the API stores directly.
  const [basic, setBasic] = useState({ title: '', description: '', fromDay: '1', toDay: '1' });
  const [basicError, setBasicError] = useState<string | null>(null);

  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [qForm, setQForm] = useState<QForm>(emptyQuestionForm());
  const [qError, setQError] = useState<string | null>(null);
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);

  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deleteFormConfirm, setDeleteFormConfirm] = useState<ChecklistForm | null>(null);
  const [deleteQConfirm, setDeleteQConfirm] = useState<ChecklistQuestion | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const fetchForms = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await onboardingAPI.getChecklistForms(selectedProjectId);
      setForms(res.data?.data ?? []);
      setForbidden(null);
    } catch (err) {
      if (httpStatus(err) === 403) {
        setForbidden(apiMessage(err, 'You do not have access to Onboarding for this project.'));
        return;
      }
      setForbidden(null);
      setError(apiMessage(err, 'Failed to load checklist forms.'));
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { void fetchForms(); }, [fetchForms]);

  const reloadForm = useCallback(async (formId: number) => {
    if (!selectedProjectId) return;
    const res = await onboardingAPI.getChecklistForm({ projectId: selectedProjectId, formId });
    setEditingForm(res.data?.data?.form ?? null);
    setQuestions(res.data?.data?.questions ?? []);
  }, [selectedProjectId]);

  /* ------------------------------------------------------------ builder open */

  const openCreate = () => {
    setEditingForm(null);
    setQuestions([]);
    setBasic({ title: '', description: '', fromDay: '1', toDay: '1' });
    setBasicError(null);
    setBasicOpen(true);
    cancelQuestionEditor();
    setBuilderOpen(true);
  };

  const openEdit = async (form: ChecklistForm) => {
    setBasic({
      title: form.title,
      description: form.description ?? '',
      // Storage is 0-based; the builder shows 1-based (Day 1 = joining day).
      fromDay: String(form.fromDay + 1),
      toDay: String(form.toDay + 1),
    });
    setBasicError(null);
    // Questions matter more than the already-filled basics when editing.
    setBasicOpen(false);
    cancelQuestionEditor();
    setBuilderOpen(true);
    setEditingForm(form);
    try {
      await reloadForm(form.id);
    } catch (err) {
      setBasicError(apiMessage(err, 'Failed to load the form.'));
    }
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setEditingForm(null);
    setQuestions([]);
    cancelQuestionEditor();
    void fetchForms();
  };

  /* ------------------------------------------------------------ basic details */

  const saveBasic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    // Inputs are 1-based (Day 1 = joining day); Day 0 is not allowed. Convert to
    // 0-based journey days for storage. Checked here so a cleared box reads as
    // "fill this in" rather than bouncing.
    const fromDisplay = Number(basic.fromDay);
    const toDisplay = Number(basic.toDay);
    if (!Number.isInteger(fromDisplay) || fromDisplay < 1 || fromDisplay > 61) {
      setBasicError('“From day” must be Day 1 or later — Day 1 is the joining day.');
      return;
    }
    if (!Number.isInteger(toDisplay) || toDisplay < fromDisplay || toDisplay > 61) {
      setBasicError('“To day” must be a whole number, not before the start day.');
      return;
    }
    const fromDay = fromDisplay - 1;
    const toDay = toDisplay - 1;
    setIsSubmitting(true);
    setBasicError(null);
    try {
      if (editingForm) {
        await onboardingAPI.updateChecklistForm({
          projectId: selectedProjectId, formId: editingForm.id,
          title: basic.title.trim(), description: null, fromDay, toDay,
        });
        await reloadForm(editingForm.id);
        setBasicOpen(false);
      } else {
        const res = await onboardingAPI.createChecklistForm({
          projectId: selectedProjectId,
          title: basic.title.trim(), description: null, fromDay, toDay,
        });
        setEditingForm(res.data?.data ?? null);
        setBasicOpen(false);
      }
      await fetchForms();
    } catch (err) {
      setBasicError(apiMessage(err, 'Failed to save the form.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (form: ChecklistForm) => {
    if (!selectedProjectId) return;
    setActionError(null);
    setNotice(null);
    setTogglingId(form.id);
    try {
      await onboardingAPI.setChecklistFormActive({
        projectId: selectedProjectId, formId: form.id, active: !form.active,
      });
      setNotice(
        !form.active
          ? `"${form.title}" is now live for Day ${form.fromDay + 1}–${form.toDay + 1}.`
          : `"${form.title}" is no longer live. Nothing will be asked on Day ${form.fromDay + 1}–${form.toDay + 1} until a form covers those days.`,
      );
      await fetchForms();
      if (editingForm?.id === form.id) await reloadForm(form.id);
    } catch (err) {
      // Server-side reasons a form cannot go live — no questions, or an
      // overlapping live range — arrive here with a specific message worth
      // showing verbatim rather than a generic failure.
      setActionError(apiMessage(err, 'Failed to change the form status.'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteForm = async (form: ChecklistForm) => {
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setActionError(null);
    try {
      await onboardingAPI.deleteChecklistForm({ projectId: selectedProjectId, formId: form.id });
      setDeleteFormConfirm(null);
      if (editingForm?.id === form.id) closeBuilder();
      await fetchForms();
    } catch (err) {
      setActionError(apiMessage(err, 'Failed to delete the form.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ------------------------------------------------------------ question CRUD */

  // Conditions and option-sources may only reference OTHER questions on the same
  // form — the buddy answers a form in one sitting, and a cross-form reference
  // would dangle the moment that form changed.
  const ruleQuestions: RuleQuestion[] = useMemo(
    () =>
      questions
        .filter((q) => q.id !== editingQuestionId)
        .map((q) => ({
          id: q.id,
          question_text: q.questionText,
          question_type: q.questionType as RuleQuestion['question_type'],
          options: q.options,
        })),
    [questions, editingQuestionId],
  );

  function cancelQuestionEditor() {
    setShowNewQuestion(false);
    setEditingQuestionId(null);
    setQError(null);
    setConditionsOpen(false);
    setValidationOpen(false);
  }

  const startNewQuestion = () => {
    setQForm(emptyQuestionForm());
    setQError(null);
    setConditionsOpen(false);
    setValidationOpen(false);
    setEditingQuestionId(null);
    setShowNewQuestion(true);
  };

  const startEditQuestion = (q: ChecklistQuestion) => {
    const v = q.rules?.validation ?? {};
    setQForm({
      question_text: q.questionText,
      question_type: q.questionType,
      options: q.options && q.options.length > 0 ? q.options : [''],
      is_required: q.isRequired,
      options_source_type: q.rules?.option_source ? 'from_question' : 'static',
      options_source: normalizeOptionSource(q.rules?.option_source ?? null),
      visibility_rules: q.rules?.visibility ?? null,
      validation_format: v.format ?? 'none',
      validation_regex: v.regex ?? '',
      validation_length_min: v.length?.min ?? '',
      validation_length_max: v.length?.max ?? '',
      validation_date_format: v.dateFormat ?? 'DD/MM/YYYY',
      validation_unique_scope: v.unique?.scope ?? 'none',
      validation_error_message: v.errorMessage ?? '',
      rating_display_mode: (q.ratingDisplayMode as QForm['rating_display_mode']) ?? 'buttons',
      rating_step: (Number(q.ratingStep) === 0.5 ? 0.5 : 1) as 0.5 | 1,
      rating_max: String(q.ratingMax ?? 5),
    });
    setQError(null);
    setConditionsOpen(!!q.rules?.visibility);
    setValidationOpen(!!q.rules?.validation);
    setShowNewQuestion(false);
    setEditingQuestionId(q.id);
  };

  /** Assemble the rules JSONB from editor state. Empty blocks are omitted. */
  const buildRules = (): QuestionRules | null => {
    const isChoice = CHOICE_TYPES.includes(qForm.question_type);
    const rules: QuestionRules = {};

    if (qForm.visibility_rules && qForm.visibility_rules.conditions.length > 0) {
      rules.visibility = qForm.visibility_rules;
    }
    if (isChoice && qForm.options_source_type === 'from_question' && qForm.options_source?.depends_on_question_id) {
      rules.option_source = qForm.options_source;
    }

    const validation: ValidationRules = {};
    if (qForm.validation_format && qForm.validation_format !== 'none') {
      validation.format = qForm.validation_format;
      if (qForm.validation_format === 'regex' && qForm.validation_regex.trim()) {
        validation.regex = qForm.validation_regex.trim();
      }
    }
    const minLen = typeof qForm.validation_length_min === 'number' ? qForm.validation_length_min : null;
    const maxLen = typeof qForm.validation_length_max === 'number' ? qForm.validation_length_max : null;
    if (minLen != null || maxLen != null) validation.length = { min: minLen, max: maxLen };
    if (qForm.question_type === 'date') validation.dateFormat = qForm.validation_date_format;
    if (qForm.validation_unique_scope && qForm.validation_unique_scope !== 'none') {
      validation.unique = { scope: qForm.validation_unique_scope };
    }
    if (qForm.validation_error_message.trim()) {
      validation.errorMessage = qForm.validation_error_message.trim();
    }
    if (Object.keys(validation).length > 0) rules.validation = validation;

    return Object.keys(rules).length > 0 ? rules : null;
  };

  const submitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId || !editingForm) return;
    const ratingMax = Number(qForm.rating_max);
    if (qForm.question_type === 'rating_1_5' && (!Number.isInteger(ratingMax) || ratingMax < 2 || ratingMax > 10)) {
      setQError('Rating scale max must be a whole number between 2 and 10.');
      return;
    }
    const isChoice = CHOICE_TYPES.includes(qForm.question_type);
    setIsSubmitting(true);
    setQError(null);
    const payload = {
      projectId: selectedProjectId,
      formId: editingForm.id,
      questionText: qForm.question_text.trim(),
      questionType: qForm.question_type,
      // Static choice questions send their list; from-question ones keep their
      // options inside rules.option_source, so the column stays null.
      options: isChoice && qForm.options_source_type === 'static'
        ? qForm.options.filter((o) => o.trim())
        : null,
      isRequired: qForm.is_required,
      rules: buildRules(),
      ratingMax,
      ratingDisplayMode: qForm.rating_display_mode,
      ratingStep: qForm.rating_step,
    };
    try {
      if (editingQuestionId) {
        await onboardingAPI.updateChecklistQuestion({ ...payload, questionId: editingQuestionId });
      } else {
        await onboardingAPI.createChecklistQuestion(payload);
      }
      cancelQuestionEditor();
      await reloadForm(editingForm.id);
      await fetchForms();
    } catch (err) {
      setQError(apiMessage(err, 'Failed to save the question.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (q: ChecklistQuestion) => {
    if (!selectedProjectId || !editingForm) return;
    setIsSubmitting(true);
    setActionError(null);
    try {
      await onboardingAPI.deleteChecklistQuestion({
        projectId: selectedProjectId, formId: editingForm.id, questionId: q.id,
      });
      setDeleteQConfirm(null);
      await reloadForm(editingForm.id);
      await fetchForms();
    } catch (err) {
      setActionError(apiMessage(err, 'Failed to delete the question.'));
      setDeleteQConfirm(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrop = async (targetId: string) => {
    if (!dragId || dragId === targetId || !selectedProjectId || !editingForm) return;
    const ids = questions.map((q) => q.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    // Optimistic so the list doesn't flicker back mid-request.
    setQuestions((prev) => [...prev].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)));
    setDragId(null);
    try {
      await onboardingAPI.reorderChecklistQuestions({
        projectId: selectedProjectId, formId: editingForm.id, questionIds: ids,
      });
    } catch (err) {
      setActionError(apiMessage(err, 'Failed to save the new order.'));
      await reloadForm(editingForm.id);
    }
  };

  /* ------------------------------------------------------------------ render */

  const newFormButton = (
    <button
      onClick={openCreate}
      className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
    >
      <Plus className="h-4 w-4" /> Create Form
    </button>
  );

  if (!selectedProjectId) {
    return (
      <div className="space-y-6">
        {!embedded && <PageHeading />}
        <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />
      </div>
    );
  }

  if (forbidden) {
    return (
      <AccessDeniedPanel
        message={forbidden}
        onLogout={() => { clearAuth(); navigate('/login'); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {!embedded && <PageHeading />}

      {actionSlot ? createPortal(newFormButton, actionSlot) : (
        <div className="flex items-center justify-end">{newFormButton}</div>
      )}

      {notice && <Banner tone="info" onDismiss={() => setNotice(null)}>{notice}</Banner>}
      {actionError && <Banner tone="error" onDismiss={() => setActionError(null)}>{actionError}</Banner>}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
            <tr>
              <th className="px-6 py-4">Form</th>
              <th className="px-6 py-4">Valid for</th>
              <th className="px-6 py-4">Questions</th>
              {/* No separate Status column — the accessible Actions switch carries the status,
                  and a chip beside it was the same fact stated twice. */}
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 4 }).map((__, j) => (
                    <td key={j} className="px-6 py-4"><Skeleton className="h-4 w-28" /></td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center">
                  <p className="text-sm text-red-500">{error}</p>
                  <button onClick={fetchForms} className="mt-2 text-sm text-blue-600 hover:underline">Try again</button>
                </td>
              </tr>
            ) : forms.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                  No checklist yet. Create one to define what a new joiner completes on each day of their
                  onboarding — and add a second form if the questions should change later in the plan.
                </td>
              </tr>
            ) : (
              forms.map((f) => (
                <tr key={f.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button onClick={() => openEdit(f)} className="text-left">
                      <div className="font-medium text-gray-900 hover:text-primary-600">{f.title}</div>
                      {f.description && <div className="text-xs text-gray-500">{f.description}</div>}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className="inline-flex items-center gap-1.5 text-gray-900">
                      <CalendarRange className="h-3.5 w-3.5 text-gray-400" />
                      {f.fromDay === f.toDay ? `Day ${f.fromDay + 1}` : `Day ${f.fromDay + 1}–${f.toDay + 1}`}
                    </span>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {f.fromDay === 0 && f.toDay === 0
                        ? 'Joining day'
                        : `${f.toDay - f.fromDay + 1} onboarding day${f.toDay === f.fromDay ? '' : 's'}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{f.questionCount ?? 0}</td>
                  <td className="px-6 py-4 text-center">
                    <TableActionGroup>
                      <TableActionButton action="edit" label="Edit"
                        onClick={() => openEdit(f)}
                        title="Edit"
                      />
                      <TableActionButton action="delete" label="Delete"
                        onClick={() => setDeleteFormConfirm(f)}
                        title="Delete"
                      />
                      <StatusToggle
                        active={f.active}
                        busy={togglingId === f.id}
                        questionCount={f.questionCount ?? 0}
                        onToggle={() => toggleActive(f)}
                      />
                    </TableActionGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Builder modal (Basic Details → Questions), mirroring Tasks ── */}
      {builderOpen && (
        <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4 py-10" role="dialog" aria-modal="true">
          <div className="absolute inset-0" onClick={closeBuilder} />
          <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between rounded-t-2xl bg-primary-700 px-6 py-4 text-white">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-primary-200">
                  Onboarding checklist
                </div>
                <h3 className="text-lg font-semibold">
                  {editingForm ? editingForm.title || 'Edit Form' : 'Create New Form'}
                </h3>
              </div>
              <button onClick={closeBuilder} className="rounded-md p-1 text-primary-100 hover:bg-white/10" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
              {/* Where am I, and what's left? The two-step save gave no sense of
                  progress, so the steps are spelled out rather than implied by a
                  disabled button. */}
              <ol className="flex flex-wrap items-center gap-2 text-xs">
                <StepChip n={1} label="Form details" state={editingForm ? 'done' : 'current'} />
                <span className="text-gray-300">→</span>
                <StepChip
                  n={2}
                  label="Add questions"
                  state={!editingForm ? 'locked' : questions.length === 0 ? 'current' : 'done'}
                />
              </ol>

              {/* Basic Details */}
              <section className="rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setBasicOpen((v) => !v)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left"
                >
                  {basicOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  <span className="text-sm font-semibold text-gray-900">
                    <span className="mr-1.5 text-gray-400">1.</span>Form details
                  </span>
                  {editingForm && (
                    <span className="ml-auto flex items-center gap-2">
                      {!basicOpen && (
                        <span className="text-xs text-gray-500">
                          {editingForm.fromDay === editingForm.toDay
                            ? `Day ${editingForm.fromDay + 1}`
                            : `Day ${editingForm.fromDay + 1}–${editingForm.toDay + 1}`}
                        </span>
                      )}
                      <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                        <CheckCircle2 className="h-3 w-3" /> Saved
                      </span>
                    </span>
                  )}
                </button>
                {basicOpen && (
                  <form onSubmit={saveBasic} className="space-y-4 border-t border-gray-100 px-4 py-4">
                    {basicError && <FormErrorBanner message={basicError} />}
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="sf-title">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="sf-title"
                        value={basic.title}
                        onChange={(e) => setBasic((p) => ({ ...p, title: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g. First-day checklist"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Valid for</label>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                        <span>From day</span>
                        <input
                          type="number"
                          // No min/max: these inputs live inside a <form>, so a
                          // native constraint failure BLOCKS submit and shows only
                          // a transient browser tooltip — the save appears to do
                          // nothing. saveBasic validates the same range and renders
                          // a persistent banner instead, which is the feedback the
                          // user actually sees.
                          value={basic.fromDay}
                          onChange={(e) => setBasic((p) => ({ ...p, fromDay: e.target.value }))}
                          className="w-20 rounded-lg border border-gray-300 px-2 py-2 text-sm"
                        />
                        <span>to day</span>
                        <input
                          type="number"
                          // No min/max: these inputs live inside a <form>, so a
                          // native constraint failure BLOCKS submit and shows only
                          // a transient browser tooltip — the save appears to do
                          // nothing. saveBasic validates the same range and renders
                          // a persistent banner instead, which is the feedback the
                          // user actually sees.
                          value={basic.toDay}
                          onChange={(e) => setBasic((p) => ({ ...p, toDay: e.target.value }))}
                          className="w-20 rounded-lg border border-gray-300 px-2 py-2 text-sm"
                        />
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                          onboarding days
                        </span>
                      </div>
                      {/* Reference point stated in full — these are JOURNEY days,
                          exactly like the onboarding plan, and an off-by-one
                          silently shifts every question by a day. */}
                      <p className="mt-1.5 text-sm font-medium text-primary-700">{describeRange(basic.fromDay, basic.toDay)}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        <strong>Day 1 = the joining day</strong> (the same numbering the onboarding plan uses — Day 2 is the day after joining). Two live forms can’t cover the same day.
                      </p>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      {!editingForm && (
                        <span className="text-xs text-gray-500">Saving unlocks the questions below.</span>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:bg-gray-300"
                      >
                        {isSubmitting ? 'Saving…' : editingForm ? 'Save changes' : 'Save & add questions'}
                      </button>
                    </div>
                  </form>
                )}
              </section>

              {/* Questions */}
              <section className="rounded-xl border border-gray-200">
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                  <span className="text-sm font-semibold text-gray-900">
                    <span className="mr-1.5 text-gray-400">2.</span>Questions
                    {questions.length > 0 && (
                      <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {questions.length}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={startNewQuestion}
                    disabled={!editingForm || showNewQuestion}
                    className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add question
                  </button>
                </div>

                <div className="space-y-2 px-4 py-4">
                  {!editingForm ? (
                    // Says WHY it's locked and what unlocks it, rather than
                    // leaving a greyed-out button to be interpreted.
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
                      <p className="text-sm font-medium text-gray-700">Questions unlock once the form exists</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Fill in the title and day range above, then choose <strong>Save &amp; add questions</strong>.
                      </p>
                    </div>
                  ) : questions.length === 0 && !showNewQuestion ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
                      <p className="text-sm font-medium text-gray-700">No questions yet</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Add the first question the new joiner will answer. A form needs at least one before it can go live.
                      </p>
                      <button
                        type="button"
                        onClick={startNewQuestion}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add question
                      </button>
                    </div>
                  ) : (
                    questions.map((q, idx) =>
                      editingQuestionId === q.id ? (
                        <QuestionEditor
                          key={q.id}
                          index={idx + 1}
                          isEdit
                          value={qForm}
                          onChange={setQForm}
                          error={qError}
                          isSubmitting={isSubmitting}
                          onCancel={cancelQuestionEditor}
                          onSubmit={submitQuestion}
                          ruleQuestions={ruleQuestions}
                          conditionsOpen={conditionsOpen}
                          setConditionsOpen={setConditionsOpen}
                          validationOpen={validationOpen}
                          setValidationOpen={setValidationOpen}
                        />
                      ) : (
                        <div
                          key={q.id}
                          draggable
                          onDragStart={() => setDragId(q.id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => handleDrop(q.id)}
                          className="group flex items-start gap-3 rounded-xl border border-gray-200 border-l-4 bg-white px-4 py-3 hover:border-gray-300"
                          style={{ borderLeftColor: TYPE_ACCENT[q.questionType] ?? '#94A3B8' }}
                        >
                          <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-gray-300 group-hover:text-gray-400" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{idx + 1}. {q.questionText}</span>
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                {QUESTION_TYPES.find((t) => t.value === q.questionType)?.label ?? q.questionType}
                              </span>
                              {q.isRequired && (
                                <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">Required</span>
                              )}
                            </div>
                            {q.options && q.options.length > 0 && (
                              <div className="mt-1 text-xs text-gray-500">Options: {q.options.join(' · ')}</div>
                            )}
                            {q.rules?.option_source && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-indigo-700">
                                <Zap className="h-3 w-3" /> Options depend on another answer
                              </div>
                            )}
                            {q.rules?.visibility && (
                              <div className="mt-1 text-xs text-violet-700">
                                Shown when {summarizeRule(q.rules.visibility, questions.map((s) => ({
                                  id: s.id,
                                  question_text: s.questionText,
                                  question_type: s.questionType as RuleQuestion['question_type'],
                                  options: s.options,
                                })))}
                              </div>
                            )}
                            {q.rules?.validation && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                                <ShieldCheck className="h-3 w-3" /> Validation applied
                              </div>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              onClick={() => startEditQuestion(q)}
                              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-primary-600"
                              title="Edit"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteQConfirm(q)}
                              className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ),
                    )
                  )}

                  {showNewQuestion && editingForm && (
                    <QuestionEditor
                      index={questions.length + 1}
                      isEdit={false}
                      value={qForm}
                      onChange={setQForm}
                      error={qError}
                      isSubmitting={isSubmitting}
                      onCancel={cancelQuestionEditor}
                      onSubmit={submitQuestion}
                      ruleQuestions={ruleQuestions}
                      conditionsOpen={conditionsOpen}
                      setConditionsOpen={setConditionsOpen}
                      validationOpen={validationOpen}
                      setValidationOpen={setValidationOpen}
                    />
                  )}
                </div>
              </section>
            </div>

            {/* States what is still needed and offers the next action, rather
                than a bare Close that leaves "did this save?" unanswered. Every
                change is already persisted as it is made — the footer says so
                explicitly, because a modal with a Close button reads as though
                there is an unsaved draft inside it. */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
              <p className="text-xs text-gray-500">
                {!editingForm
                  ? 'Nothing is saved yet — save the form details to begin.'
                  : editingForm.active
                    ? 'Changes are saved as you make them. This form is live.'
                    : questions.length === 0
                      ? 'Changes are saved as you make them. Add a question to be able to make it live.'
                      : 'Changes are saved as you make them. This form is still a draft.'}
              </p>
              {/* Live/Draft is deliberately NOT offered here — it is one switch
                  on the forms list, and a second copy in the builder meant the
                  same fact had two controls. */}
              <button
                onClick={closeBuilder}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {editingForm ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={!!deleteFormConfirm}
        onClose={() => setDeleteFormConfirm(null)}
        onConfirm={() => deleteFormConfirm && handleDeleteForm(deleteFormConfirm)}
        isSubmitting={isSubmitting}
        title="Delete Form"
        entityLabel="checklist form"
        entityName={deleteFormConfirm?.title}
        consequenceNote="Every question on this form is deleted with it. If it was live, nothing will be asked on the days it covered until another form covers them."
      />

      <ConfirmDeleteModal
        isOpen={!!deleteQConfirm}
        onClose={() => setDeleteQConfirm(null)}
        onConfirm={() => deleteQConfirm && handleDeleteQuestion(deleteQConfirm)}
        isSubmitting={isSubmitting}
        title="Delete Question"
        entityLabel="question"
        entityName={deleteQConfirm?.questionText}
        confirmLabel="Delete"
      />
    </div>
  );
}

/* ------------------------------------------------------------- sub-components */

/**
 * Live/Draft as a single switch.
 *
 * Disabled with a reason when the form has no questions — the server refuses to
 * activate an empty form, and a toggle that silently snaps back is worse than
 * one that says why it will not move.
 */
function StatusToggle({
  active, busy, questionCount, onToggle,
}: { active: boolean; busy: boolean; questionCount: number; onToggle: () => void }) {
  const blocked = !active && questionCount === 0;
  return (
    <TableActionButton
      action={active ? 'deactivate' : 'activate'}
      label={active ? 'Live — click to move back to draft' : 'Draft — click to make live'}
      loading={busy}
      type="button"
      role="switch"
      aria-checked={active}
      disabled={busy || blocked}
      onClick={onToggle}
      title={
        blocked
          ? 'Add at least one question before making this form live'
          : active ? 'Live — click to move back to draft' : 'Draft — click to make live'
      }
    />
  );
}

/** One step in the builder's progress rail. */
function StepChip({ n, label, state }: { n: number; label: string; state: 'done' | 'current' | 'locked' }) {
  const cls =
    state === 'done' ? 'bg-green-50 text-green-700'
      : state === 'current' ? 'bg-primary-600 text-white'
      : 'bg-gray-100 text-gray-400';
  return (
    <li className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold ${cls}`}>
      {state === 'done'
        ? <CheckCircle2 className="h-3.5 w-3.5" />
        : <span className="flex h-3.5 w-3.5 items-center justify-center text-[10px]">{n}</span>}
      {label}
    </li>
  );
}

function PageHeading() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Onboarding Checklist</h1>
      <p className="mt-1 text-sm text-gray-500">
        The questions a new joiner completes themselves, day by day, across their onboarding plan. Each form
        covers a stretch of the plan, counted from that joiner's own joining day (Day 1).
      </p>
    </div>
  );
}

function Banner({
  tone, children, onDismiss,
}: { tone: 'info' | 'error'; children: React.ReactNode; onDismiss: () => void }) {
  const cls = tone === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-blue-200 bg-blue-50 text-blue-800';
  return (
    <div className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${cls}`}>
      <span className="flex items-start gap-2">
        {tone === 'error' ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />}
        {children}
      </span>
      <button type="button" onClick={onDismiss} className="shrink-0 rounded p-0.5 hover:bg-black/5" aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * The question editor. Deliberately the same shape and order as the Tasks
 * editor — question text → options source → answer type → static options →
 * required → type-specific config → conditions → validation — and driven by the
 * very same OptionSourceEditor / RuleBuilder components.
 */
function QuestionEditor({
  index, isEdit, value, onChange, error, isSubmitting, onCancel, onSubmit,
  ruleQuestions, conditionsOpen, setConditionsOpen, validationOpen, setValidationOpen,
}: {
  index: number;
  isEdit: boolean;
  value: QForm;
  onChange: (v: QForm) => void;
  error: string | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
  ruleQuestions: RuleQuestion[];
  conditionsOpen: boolean;
  setConditionsOpen: (v: boolean) => void;
  validationOpen: boolean;
  setValidationOpen: (v: boolean) => void;
}) {
  const isChoice = CHOICE_TYPES.includes(value.question_type);
  const fromQuestion = isChoice && value.options_source_type === 'from_question';
  const set = (patch: Partial<QForm>) => onChange({ ...value, ...patch });

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border-2 border-primary-200 bg-primary-50/30 p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-600 text-xs font-semibold text-white">
          {index}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">
          {isEdit ? 'Edit question' : 'New question'}
        </span>
      </div>

      {error && <FormErrorBanner message={error} />}

      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">Question Text</label>
        <textarea
          value={value.question_text}
          onChange={(e) => set({ question_text: e.target.value })}
          className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="e.g. Did the new joiner complete the store opening checklist today?"
          rows={2}
          required
        />
      </div>

      {/* Options source tiles first — Static vs Depends-on-another-question is
          the primary decision, same ordering as Tasks. */}
      {isChoice && (
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Options</label>
          <OptionSourceEditor
            sourceType={value.options_source_type}
            onSourceTypeChange={(t) => set({ options_source_type: t })}
            source={value.options_source}
            onSourceChange={(src) => set({ options_source: src })}
            currentQuestionType={value.question_type as never}
            availableQuestions={ruleQuestions}
            currentQuestionId={null}
          />
        </div>
      )}

      {/* Answer type is hidden in from-question mode: the type is chosen per
          mapping row above, so a top-level selector would contradict it. */}
      {!fromQuestion && (
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Answer Type</label>
          <select
            value={value.question_type}
            onChange={(e) => {
              const nextType = e.target.value as QuestionType;
              const nextIsChoice = CHOICE_TYPES.includes(nextType);
              set({
                question_type: nextType,
                options_source_type: nextIsChoice ? value.options_source_type : 'static',
                options_source: nextIsChoice ? value.options_source : null,
              });
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      )}

      {isChoice && value.options_source_type === 'static' && (
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">
            {value.question_type === 'dropdown' ? 'Dropdown options'
              : value.question_type === 'single_choice' ? 'Single-choice options'
              : 'Multiple-choice options'}
          </label>
          <StaticOptionsEditor options={value.options} onChange={(opts) => set({ options: opts })} />
        </div>
      )}

      <div className="flex items-center gap-3 py-1">
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={value.is_required}
            onChange={(e) => set({ is_required: e.target.checked })}
            className="peer sr-only"
          />
          <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-primary-300" />
          <span className="ml-2 text-sm text-gray-700">Required</span>
        </label>
      </div>

      {value.question_type === 'rating_1_5' && (
        <div className="space-y-2 rounded-md border border-primary-200 bg-primary-50/40 p-3">
          <p className="text-xs text-primary-900">
            The buddy sees a constrained 1–{value.rating_max || '?'} picker. Choose the scale, widget style and step.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Max rating</label>
              <input
                type="number" min={2} max={10} step={1}
                value={value.rating_max}
                onChange={(e) => set({ rating_max: e.target.value })}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Picker style</label>
              <select
                value={value.rating_display_mode}
                onChange={(e) => set({ rating_display_mode: e.target.value as QForm['rating_display_mode'] })}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                <option value="buttons">Buttons</option>
                <option value="stars">Stars</option>
                <option value="slider">Slider</option>
                <option value="dropdown">Dropdown</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Step</label>
              <select
                value={String(value.rating_step)}
                onChange={(e) => set({ rating_step: e.target.value === '0.5' ? 0.5 : 1 })}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                <option value="1">Whole numbers (1, 2, 3, …)</option>
                <option value="0.5">Half steps (1, 1.5, …)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Conditional logic */}
      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => {
            const next = !conditionsOpen;
            setConditionsOpen(next);
            if (!next) set({ visibility_rules: null });
          }}
          className="group flex w-full items-center gap-2 text-left"
        >
          {conditionsOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <Zap className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-xs font-semibold text-gray-700">Conditional logic</span>
          <span className="text-[11px] text-gray-400">only ask this in certain cases</span>
        </button>
        {conditionsOpen && (
          <div className="mt-2">
            {ruleQuestions.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-xs text-gray-500">
                Conditions reference other questions on this form. Add another question first.
              </p>
            ) : (
              <RuleBuilder
                rule={value.visibility_rules}
                onChange={(next) => set({ visibility_rules: next })}
                availableQuestions={ruleQuestions}
              />
            )}
          </div>
        )}
      </div>

      {/* Validation */}
      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => setValidationOpen(!validationOpen)}
          className="group flex w-full items-center gap-2 text-left"
        >
          {validationOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
          <span className="text-xs font-semibold text-gray-700">Validation</span>
          <span className="text-[11px] text-gray-400">optional — leave blank for no rule</span>
        </button>
        {validationOpen && (
          <div className="mt-2 space-y-3 rounded-lg border border-gray-200 bg-white p-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Format</label>
                <select
                  value={value.validation_format}
                  onChange={(e) => set({ validation_format: e.target.value as QForm['validation_format'] })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                >
                  <option value="none">No format rule</option>
                  <option value="letters">Letters only</option>
                  <option value="numbers">Numbers only</option>
                  <option value="alphanumeric">Letters and numbers</option>
                  <option value="email">Email address</option>
                  <option value="integer">Whole number</option>
                  <option value="positive">Positive number</option>
                  <option value="regex">Custom pattern (regex)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Must be unique</label>
                <select
                  value={value.validation_unique_scope}
                  onChange={(e) => set({ validation_unique_scope: e.target.value as QForm['validation_unique_scope'] })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                >
                  <option value="none">No uniqueness rule</option>
                  <option value="question">Within this question</option>
                  <option value="form">Within this form</option>
                  <option value="project">Across the project</option>
                  <option value="global">Everywhere</option>
                </select>
              </div>
            </div>

            {value.validation_format === 'regex' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Pattern</label>
                <input
                  value={value.validation_regex}
                  onChange={(e) => set({ validation_regex: e.target.value })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  placeholder="^[A-Z]{2}[0-9]{4}$"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Min length / value</label>
                <input
                  type="number"
                  value={value.validation_length_min}
                  onChange={(e) => set({ validation_length_min: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Max length / value</label>
                <input
                  type="number"
                  value={value.validation_length_max}
                  onChange={(e) => set({ validation_length_max: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                />
              </div>
            </div>

            {value.question_type === 'date' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Date format</label>
                <select
                  value={value.validation_date_format}
                  onChange={(e) => set({ validation_date_format: e.target.value as QForm['validation_date_format'] })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="DD-MMM-YYYY">DD-MMM-YYYY</option>
                  <option value="long">Long (1 January 2026)</option>
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Message shown when it fails</label>
              <input
                value={value.validation_error_message}
                onChange={(e) => set({ validation_error_message: e.target.value })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                placeholder="Leave blank for the default message"
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-primary-100 pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:bg-gray-300"
        >
          {isSubmitting ? 'Saving…' : 'Save question'}
        </button>
      </div>
    </form>
  );
}
